import { z } from 'zod';
import { carbonTargetRowSchema } from './sustainability.js';
import type { GhgCategoryKey } from '../use-phase/scenario-carbon-kernel.js';

/**
 * Wire DTOs + pure helpers for the SIL-442 (W4) baseline **trajectory
 * dashboard** — `GET /api/client/sustainability/trajectory`, spec §6/§9 of
 * resources/plans/sustainability-forecasting-spec.md.
 *
 * This is the "hold everything" lens: no scenario, no actions, no advisory
 * numbers — just the org's whole RLS-scoped fleet held untouched across a
 * horizon, drawn against the org carbon target, plus the §9 data-coverage
 * strip. The arithmetic is NOT reimplemented here: the API layer feeds a
 * single `hold` cohort to the SIL-435 carbon kernel
 * (`buildCarbonScenarioSeries`) and flattens its baseline leg into
 * `SustainabilityTrajectoryPoint`s.
 *
 * SHIPS CAVEATED, NEVER GATED (spec §9, sign-off decision): a thin coverage
 * figure suppresses NOTHING — the strip states months-available and
 * %-of-fleet-covered and the chart renders regardless. Do not add a
 * minimum-coverage gate here or in any caller.
 *
 * Bare-JSON success responses (no `{data:...}` envelope), matching
 * sustainability.ts / financials.ts.
 */

// ---------------------------------------------------------------------------
// 1. Horizon
// ---------------------------------------------------------------------------

/** Same option set as `financial_scenario.horizon_months`'s DB CHECK — the
 * trajectory is horizon-shaped exactly like a scenario so the two read on
 * the same mental scale. */
export const SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS = [12, 24, 36, 60] as const;
export type SustainabilityTrajectoryHorizon = (typeof SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS)[number];

export const SUSTAINABILITY_TRAJECTORY_DEFAULT_HORIZON: SustainabilityTrajectoryHorizon = 60;

export const sustainabilityTrajectoryHorizonSchema = z.coerce
  .number()
  .int()
  .refine(
    (n): n is SustainabilityTrajectoryHorizon =>
      (SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS as readonly number[]).includes(n),
    { message: `horizonMonths must be one of ${SUSTAINABILITY_TRAJECTORY_HORIZON_OPTIONS.join(', ')}` },
  );

// ---------------------------------------------------------------------------
// 2. Data-coverage strip (spec §9)
// ---------------------------------------------------------------------------

/**
 * The §9 readiness strip: "months available, % of fleet covered". Caveat
 * copy only — never a gate.
 *
 * `coveredAssetCount` counts assets with at least one computed
 * `asset_use_phase_snapshot`; the remainder contribute a true 0 to the
 * trajectory, which is a DISCLOSED gap (the strip says so), not a claim
 * that they emit nothing.
 */
export const sustainabilityCoverageSchema = z.object({
  fleetAssetCount: z.number().int().nonnegative(),
  coveredAssetCount: z.number().int().nonnegative(),
  /** 0..100, one decimal. 0 when the fleet is empty (never NaN). */
  coveragePct: z.number().min(0).max(100),
  /** Distinct `period_key` months with at least one snapshot across the
   * org's fleet — "history deepens monthly" (spec §9). */
  monthsAvailable: z.number().int().nonnegative(),
  /** ISO date (YYYY-MM-DD) of the earliest/latest snapshot period, or null
   * when the org has no snapshots at all. */
  earliestPeriodKey: z.string().nullable(),
  latestPeriodKey: z.string().nullable(),
});
export type SustainabilityCoverage = z.infer<typeof sustainabilityCoverageSchema>;

/** @pure — 0 for an empty fleet (never NaN), one decimal otherwise. */
export function resolveCoveragePct(coveredAssetCount: number, fleetAssetCount: number): number {
  if (fleetAssetCount <= 0) return 0;
  return Math.round((coveredAssetCount / fleetAssetCount) * 1000) / 10;
}

// ---------------------------------------------------------------------------
// 3. Trajectory series
// ---------------------------------------------------------------------------

/**
 * One month of the hold-everything baseline, flattened from the kernel's
 * `CarbonScenarioSeriesPoint.baseline` (cumulative-to-date, per the kernel's
 * own convention — month m carries everything accrued through m).
 *
 * `scenario`/`delta` are deliberately absent: this surface has no scenario
 * leg to compare against, and emitting a delta of zeros would invite a
 * reader to interpret it as "no change available".
 */
export const sustainabilityTrajectoryPointSchema = z.object({
  month: z.number().int().nonnegative(),
  /** Per-GHG-category cumulative kgCO2e, keyed by the kernel's own
   * `GhgCategoryKey` (labels: `ESG_CATEGORY_LABELS`). */
  categories: z.object({
    scope2: z.number(),
    cat8: z.number(),
    cat1_or_2: z.number(),
    cat5: z.number(),
    cat4: z.number(),
  }),
  /** Sum of `categories` — precomputed so the chart never re-adds it. */
  totalKgco2e: z.number(),
  /** The org target's cumulative budget through this month, kgCO2e — null
   * when the org has set no target row. See `cumulativeTargetKgco2eAtMonth`. */
  targetCumulativeKgco2e: z.number().nullable(),
});
export type SustainabilityTrajectoryPoint = z.infer<typeof sustainabilityTrajectoryPointSchema>;

/**
 * The org target is an ABSOLUTE FLAT ANNUAL BUDGET in tCO2e/yr (spec §4,
 * SIL-423) — drawn against a CUMULATIVE forecast series it must therefore
 * accrue pro-rata by month, exactly the way the kernel accrues an annual
 * operate rate (`annualRate x months / 12`). Same whole-month convention;
 * do not switch this to a flat horizontal line against a cumulative series
 * (the two would not be comparable at any month but the last).
 *
 * @pure
 */
export function cumulativeTargetKgco2eAtMonth(tco2ePerYr: number, month: number): number {
  return Math.round(((tco2ePerYr * 1000 * month) / 12) * 1000) / 1000;
}

/** §12 chart label, verbatim shape: "Org target: {N} tCO2e/yr". @pure */
export function orgTargetChartLabel(tco2ePerYr: number): string {
  return `Org target: ${tco2ePerYr.toLocaleString('en-US')} tCO2e/yr`;
}

// ---------------------------------------------------------------------------
// 4. Response
// ---------------------------------------------------------------------------

export const sustainabilityTrajectoryResponseSchema = z.object({
  horizonMonths: z.number().int().positive(),
  /** Resolved PUE + which rung resolved it — disclosed inline on the card,
   * never only in a tooltip (the use-phase plan's "PUE — applied, not
   * footnoted" rule). There is no scenario here to carry a manual PUE, so
   * this is `platform_default` today; the field exists so the surface does
   * not have to change shape when an org-level PUE lands. */
  pue: z.object({
    value: z.number().positive(),
    source: z.enum(['manual_entry', 'platform_default']),
  }),
  /** From `customer_org_sustainability_config.operated` — decides whether
   * operational emissions tag `scope2` or `cat8` (copy §4). */
  operated: z.boolean(),
  /** Whole-fleet Scope-2-equivalent annual rate (device rate x PUE), kgCO2e/yr. */
  fleetAnnualRateKgco2ePerYr: z.number().nonnegative(),
  series: z.array(sustainabilityTrajectoryPointSchema),
  orgTarget: carbonTargetRowSchema.nullable(),
  coverage: sustainabilityCoverageSchema,
});
export type SustainabilityTrajectoryResponse = z.infer<typeof sustainabilityTrajectoryResponseSchema>;

/** The category keys this surface renders, in stacking order (operational
 * first, then the lump categories) — a `GhgCategoryKey[]` so a label lookup
 * against `ESG_CATEGORY_LABELS` can never desync. */
export const SUSTAINABILITY_TRAJECTORY_CATEGORY_ORDER: readonly GhgCategoryKey[] = [
  'scope2',
  'cat8',
  'cat1_or_2',
  'cat5',
  'cat4',
] as const;
